import { GooglePay } from '@adyen/adyen-web'
import { useCallback, useEffect, useRef, useState } from 'react'

import { AdyenCheckoutStatus } from '@/lib/enums'
import { normalizeWalletBrand } from '@/lib/wallet-brand'

import type { ErrorWithState, SessionResult } from '@/lib/types'

interface UseGooglePayOptions {
  /** Reads the checkout core created by the Card flow's `mount()`; `null` until then. */
  getCheckout: () => unknown | null
  /** Reads the active Adyen session id, set by the Card flow's `mount()`. */
  getSessionId: () => string | null
  /** Claims the shared session against the internal API. */
  claimSession: (sessionId: string) => Promise<unknown>
  /** Resolves once one of the shared Adyen callbacks settles the session result. */
  waitForSessionResult: () => Promise<SessionResult>
  /** Shared checkout status setter — Google Pay and Card drive the same status. */
  setStatus: (status: AdyenCheckoutStatus) => void
  /** Reads the shared checkout status — Card, Apple Pay, and Google Pay all drive the same value. */
  getStatus: () => AdyenCheckoutStatus
  /**
   * Writes the shared `brand`/`endDigits` refs the post-payment chain reads
   * (`onPaymentCompletedPayment`'s guard) — see `useApplePay`'s identical seam.
   */
  setWalletBrandInfo: (brand: string, endDigits: string) => void
  /**
   * Fired once the session result settles for a Google Pay attempt — the
   * hook drives this itself since, unlike Apple Pay, no route call submits
   * and awaits the outcome.
   */
  onCompleted?: (result: SessionResult) => void
  /** Fired when the shopper dismisses the Google Pay sheet. */
  onCancelled?: () => void
  /** Fired on a non-cancellation component error, tagged `state.isValid: true`. */
  onError?: (error: ErrorWithState) => void
  /**
   * Fired synchronously at the start of Google's own `onClick`, before the
   * claim and the outcome await — the route's only hook into "a Google Pay
   * attempt began", since the button lives inside the Adyen component and no
   * route-owned submit call wraps it. `/hpp-session` uses this to emit
   * `card-payment-started` under its existing `submitting` dedupe guard.
   */
  onSubmitAttempt?: () => void
}

interface UseGooglePayReturn {
  /** True once the mounted GooglePay component's `isAvailable()` resolves. */
  googlePayAvailable: boolean
  /** See specs/app/index.md::Apple Pay (Google Pay mirrors the mount contract). */
  mountGooglePay: (el: HTMLElement) => Promise<void>
  /** Reads the in-flight claim promise fired from `onClick`, or `null`. */
  getPendingClaim: () => Promise<unknown> | null
}

/**
 * Owns the Adyen GooglePay component on top of the checkout core the Card
 * flow (`useAdyenCheckout`) creates. Unlike Apple Pay, Adyen renders Google's
 * own button (`showPayButton: true`) and drives submission itself — there is
 * no `submitGooglePay` for a route to call, so this hook awaits the session
 * result itself once the shopper clicks, racing the shared promise against a
 * per-attempt abort channel exactly as `useApplePay`'s `submitApplePay` does,
 * so a cancelled/errored attempt never leaves a stale awaiter on the shared
 * promise for a later Card or Apple Pay attempt to settle.
 */
export function useGooglePay(options: UseGooglePayOptions): UseGooglePayReturn {
  const googlePayComponentRef = useRef<unknown>(null)
  const googlePayMountedElRef = useRef<HTMLElement | null>(null)
  const googlePayClaimRef = useRef<Promise<unknown> | null>(null)
  const rejectGooglePayAttemptRef = useRef<((error: Error) => void) | null>(null)
  const [googlePayAvailable, setGooglePayAvailable] = useState(false)

  const awaitGooglePayOutcome = useCallback(async () => {
    const attempt = new Promise<never>((_, reject) => {
      rejectGooglePayAttemptRef.current = reject
    })
    const sessionResultPromise = options.waitForSessionResult()

    try {
      const result = await Promise.race([sessionResultPromise, attempt])
      options.setStatus(result.refusal ? AdyenCheckoutStatus.FAILED : AdyenCheckoutStatus.COMPLETED)
      options.onCompleted?.(result)
    } catch (error) {
      options.setStatus(AdyenCheckoutStatus.READY)
      const e = error instanceof Error ? error : new Error(String(error))
      if (e.name === 'CANCEL') {
        options.onCancelled?.()
      } else {
        console.error('useGooglePay.mountGooglePay: attempt failed', e)
        options.onError?.(e as ErrorWithState)
      }
    } finally {
      rejectGooglePayAttemptRef.current = null
    }
  }, [options.waitForSessionResult, options.setStatus, options.onCompleted, options.onCancelled, options.onError])

  const mountGooglePay = useCallback(
    async (el: HTMLElement) => {
      // Re-entry guard: the parent mount effect re-runs on unrelated re-renders. See specs/app/index.md::Apple Pay.
      if (googlePayMountedElRef.current === el) return
      // Claimed synchronously before the isAvailable() await below, so two overlapping calls can't both pass the check above.
      googlePayMountedElRef.current = el

      const checkout = options.getCheckout() as { paymentMethodsResponse: { has: (type: string) => boolean } } | null
      if (!checkout) return
      // The method surfaces under either tx variant depending on merchant account configuration — see specs/app/index.md::Apple Pay.
      const hasGooglePay =
        checkout.paymentMethodsResponse.has('googlepay') || checkout.paymentMethodsResponse.has('paywithgoogle')
      if (!hasGooglePay) return

      const googlePayConfig = {
        showPayButton: true,
        onClick: (resolve: () => void, reject: () => void) => {
          // Refuse whenever any payment is already in flight — a shared lock, not a Google-Pay-local one. See specs/app/index.md::Google Pay.
          if (options.getStatus() !== AdyenCheckoutStatus.READY) {
            reject()
            return
          }
          options.onSubmitAttempt?.()
          const sessionId = options.getSessionId()
          if (!sessionId) {
            reject()
            return
          }
          googlePayClaimRef.current = options.claimSession(sessionId)
          googlePayClaimRef.current.catch(() => {}) // see specs/app/index.md::Decisions
          resolve()
          options.setStatus(AdyenCheckoutStatus.SUBMITTING)
          void awaitGooglePayOutcome()
        },
        onAuthorized: (
          data: { authorizedEvent: { paymentMethodData: { info: { cardNetwork: string; cardDetails: string } } } },
          actions: { resolve: () => void; reject: (error?: unknown) => void }
        ) => {
          const { cardNetwork, cardDetails } = data.authorizedEvent.paymentMethodData.info
          options.setWalletBrandInfo(normalizeWalletBrand(cardNetwork), cardDetails)
          actions.resolve()
        },
        onError: (error: unknown) => {
          const e = error instanceof Error ? error : new Error(String(error))
          if (e.name !== 'CANCEL') {
            Object.assign(e, { state: { isValid: true } })
          }
          rejectGooglePayAttemptRef.current?.(e)
        },
      }
      // biome-ignore lint/suspicious/noExplicitAny: Adyen SDK 6 types are loose
      const googlePay = new GooglePay(checkout as any, googlePayConfig as any)

      try {
        await googlePay.isAvailable()
      } catch {
        googlePayMountedElRef.current = null // unclaim: this element never got a component, a retry must be allowed
        setGooglePayAvailable(false)
        return
      }

      googlePay.mount(el)
      googlePayComponentRef.current = googlePay
      setGooglePayAvailable(true)
    },
    [
      options.getCheckout,
      options.getSessionId,
      options.getStatus,
      options.claimSession,
      options.setStatus,
      options.setWalletBrandInfo,
      options.onSubmitAttempt,
      awaitGooglePayOutcome,
    ]
  )

  const getPendingClaim = useCallback(() => googlePayClaimRef.current, [])

  useEffect(() => {
    return () => {
      const googlePayComponent = googlePayComponentRef.current as { unmount?: () => void } | null
      try {
        googlePayComponent?.unmount?.()
      } catch {}
    }
  }, [])

  return { googlePayAvailable, mountGooglePay, getPendingClaim }
}
